/**
 * SyncStatusCard Component
 *
 * Displays real-time sync status, last sync results, and manual sync trigger.
 * Shows sync progress, errors, and success messages.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertCircle, AlertTriangle } from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Chip,
  Alert
} from '@enfomentis/ui';
import type { SyncStatus } from '@/services/sync/BackgroundSyncService';
import type { SyncResult } from '@/types/sync/SyncExport';
import type { SchemaVersionError } from '@/types/sync';
import type { ProviderKey } from '@/types/sync/ProviderTypes';
import { getSchemaVersionErrorMessage } from '@/utils/syncErrorMessages';

export interface SyncStatusCardProps {
  /** Current sync status */
  status: SyncStatus;
  /** Last sync result details */
  lastSyncResult: SyncResult | null;
  /** Callback to trigger manual sync */
  onSyncNow: () => Promise<void>;
  /** Whether a provider is connected */
  isConnected: boolean;
  /** Database-sourced lastSyncAt (heartbeat - when sync process ran) */
  lastSyncAt?: string | null;
  /** Database-sourced lastExportedAt (when data was written to remote) */
  lastExportedAt?: string | null;
  /** Database-sourced lastImportedAt (when data was read from remote) */
  lastImportedAt?: string | null;
  /** Current schema version error if present */
  schemaVersionError?: SchemaVersionError | null;
  /** Callback to trigger reset confirmation modal */
  onResetClick?: () => void;
  /** Loading state during reset operation */
  isResetting?: boolean;
  /** Runtime provider (source of truth), NOT from userSettings (avoid drift during connect/disconnect) */
  connectedProvider?: ProviderKey | 'none';
}

/**
 * Sync Status Card
 *
 * Real-time display of sync status with manual sync trigger.
 * Shows status chip, last sync info, and sync button.
 */
export const SyncStatusCard: React.FC<SyncStatusCardProps> = ({
  status,
  lastSyncResult,
  onSyncNow,
  isConnected,
  lastSyncAt,
  lastExportedAt,
  lastImportedAt,
  schemaVersionError,
  onResetClick,
  isResetting = false,
  connectedProvider
}) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [, setRefreshTrigger] = useState(0);

  // Force re-render every minute to update relative timestamps ("Just now" -> "1 min ago")
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, []);

  const handleSyncNow = async (): Promise<void> => {
    setIsSyncing(true);
    try {
      await onSyncNow();
    } finally {
      setIsSyncing(false);
    }
  };

  // Status chip configuration
  const getStatusConfig = (): { color: 'default' | 'success' | 'warning' | 'danger' | 'primary'; icon: React.ReactNode; label: string } => {
    // Prioritize schema version errors over regular status
    if (schemaVersionError) {
      if (schemaVersionError.code === 'SCHEMA_VERSION_NEWER') {
        return { color: 'warning', icon: <AlertTriangle className="w-4 h-4" />, label: 'Sync Paused' };
      }
      // SCHEMA_VERSION_MISSING, SCHEMA_VERSION_INVALID, SCHEMA_VERSION_MISMATCH
      return { color: 'danger', icon: <AlertCircle className="w-4 h-4" />, label: 'Sync Error' };
    }

    switch (status) {
      case 'idle':
        return { color: 'default', icon: <Clock className="w-4 h-4" />, label: 'Idle' };
      case 'syncing':
        return { color: 'primary', icon: <RefreshCw className="w-4 h-4 animate-spin" />, label: 'Syncing...' };
      case 'success':
        return { color: 'success', icon: <CheckCircle className="w-4 h-4" />, label: 'Synced' };
      case 'error':
        return { color: 'danger', icon: <XCircle className="w-4 h-4" />, label: 'Error' };
      default:
        return { color: 'default', icon: <Clock className="w-4 h-4" />, label: 'Unknown' };
    }
  };

  const statusConfig = getStatusConfig();

  // Format last sync timestamp
  const formatLastSync = (timestamp: string | undefined): string => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  // Show placeholder when not connected
  if (!isConnected) {
    return (
      <Card>
        <CardHeader className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-text-secondary" />
            <h3 className="text-lg font-semibold">Sync Status - Unavailable</h3>
        </CardHeader>
        <CardBody className="flex flex-col items-center justify-center mb-4 space-y-4">
          <div className="text-center space-y-2">
            <p className="text-text-primary font-medium">No sync location connected</p>
            <p className="text-text-secondary text-sm max-w-sm break-words">
              Create or re-establish your provider connection to enable automatic syncing and track sync status.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <RefreshCw className="w-5 h-5 text-text-secondary" />
        <h3 className="text-lg font-semibold">Sync Status</h3>
        <div className="ml-auto flex items-center gap-2">
          <Chip
            size="sm"
            color={statusConfig.color}
            variant="flat"
            startContent={statusConfig.icon}
          >
            {statusConfig.label}
          </Chip>
        </div>
      </CardHeader>
      <CardBody className="space-y-4 p-4 sm:p-6">
        {/* Last Sync Info - Three distinct timestamps */}
        <div className="space-y-2">
          {/* Last Sync: heartbeat showing when sync process ran (checked for updates) */}
          <div className="flex items-center justify-between text-sm gap-2">
            <span className="text-text-secondary">Last Sync:</span>
            <span className="font-medium break-words text-right">
              {formatLastSync(lastSyncAt ?? undefined)}
            </span>
          </div>

          {/* Last Export: when data was written to remote (DB or ephemeral) */}
          <div className="flex items-center justify-between text-sm gap-2">
            <span className="text-text-secondary">Last Export:</span>
            <span className="font-medium break-words text-right">
              {formatLastSync(lastExportedAt ?? lastSyncResult?.dataExportedAt ?? undefined)}
            </span>
          </div>

          {/* Last Import: when data was read from remote (DB or ephemeral) */}
          <div className="flex items-center justify-between text-sm gap-2">
            <span className="text-text-secondary">Last Import:</span>
            <span className="font-medium break-words text-right">
              {formatLastSync(lastImportedAt ?? lastSyncResult?.dataImportedAt ?? undefined)}
            </span>
          </div>
        </div>

        {/* Error Alert */}
        {status === 'error' && lastSyncResult?.error && !schemaVersionError && (
          <Alert color="danger" variant="bordered">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-sm">Sync Failed</p>
                <p className="text-xs text-text-secondary mt-1">
                  {lastSyncResult.error}
                </p>
              </div>
            </div>
          </Alert>
        )}

        {/* Schema Version Error Alerts */}
        {schemaVersionError && schemaVersionError.code === 'SCHEMA_VERSION_NEWER' && (() => {
          const errorMessage = getSchemaVersionErrorMessage(schemaVersionError, 'banner');

          return (
            <Alert color="warning" variant="bordered">
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{errorMessage.title}</p>
                    <p className="text-xs text-text-secondary mt-1">
                      {errorMessage.description}
                    </p>
                  </div>
                </div>
                <Button
                  color="warning"
                  variant="flat"
                  size="sm"
                  onPress={() => window.location.reload()}
                  fullWidth
                >
                  Refresh Page
                </Button>
              </div>
            </Alert>
          );
        })()}

        {schemaVersionError &&
         schemaVersionError.code !== 'SCHEMA_VERSION_NEWER' &&
         connectedProvider &&
         connectedProvider !== 'none' && (() => {
          const errorMessage = getSchemaVersionErrorMessage(schemaVersionError, 'banner');

          const providerSpecificText = connectedProvider === 'google-drive'
            ? 'Alternatively, disconnect and switch to a different Google account or Local Directory.'
            : 'Alternatively, disconnect and choose a different folder.';

          return (
            <Alert color="danger" variant="bordered">
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{errorMessage.title}</p>
                    <p className="text-xs text-text-secondary mt-1">
                      {errorMessage.description}
                    </p>
                    <p className="text-xs text-text-tertiary mt-2">
                      {providerSpecificText}
                    </p>
                  </div>
                </div>
                <Button
                  color="danger"
                  variant="flat"
                  size="sm"
                  onPress={() => onResetClick?.()}
                  isLoading={isResetting}
                  isDisabled={isResetting}
                  fullWidth
                >
                  {isResetting ? 'Resetting...' : 'Reset Sync'}
                </Button>
              </div>
            </Alert>
          );
        })()}

        {/* Manual Sync Button - Always available when connected, regardless of auto-sync setting */}
        <Button
          color="primary"
          variant="flat"
          size="lg"
          startContent={<RefreshCw className="w-4 h-4" />}
          onPress={handleSyncNow}
          isLoading={isSyncing || status === 'syncing'}
          isDisabled={status === 'syncing' || !isConnected || !!schemaVersionError}
          fullWidth
        >
          {status === 'syncing' ? 'Syncing...' : 'Sync Now'}
        </Button>
      </CardBody>
    </Card>
  );
};
